/**
 * 福島県の背景ツアー「福島めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 鶴ヶ城の天守は昭和40年（1965年）の再建である。江戸時代から残る天守ではない。豆知識で必ず触れる。
 * - 飯盛山は白虎隊十九士の墓があり、会津若松市の計画書が「慰霊」と明記し、年2回の墓前祭が
 *   いまも行われている。solemn を立て、墓碑そのものは描かない。山の中腹から城下を見下ろす眺めにする。
 * - 東日本大震災・原子力災害伝承館は、建物の外観について公式に記述がなく、公式が商業目的の撮影を
 *   禁じている。描けないので入れていない。請戸小学校も公式の記述が少なすぎるので入れていない。
 * - あぶくま洞は照明の演出がある施設なので、色つきの照明ではなく石灰岩そのものの色で描く。
 * - 大内宿の主屋にはいまも人が住んでいる。住人・看板・のれん・店名を描かない。
 * - 浄土平天文台の建物は描かない。噴火口と荒れ地だけにする。
 * - 尾瀬の「1センチの足跡の回復に10年以上」は福島県の記述そのまま。
 * - 三春滝桜は「桜の名所」ではなく、大正11年指定の天然記念物である一本の巨木として扱う。
 */

import type { StageTour } from '../types';

export const fukushimaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'miharu-takizakura',
      name: '三春滝桜',
      area: '三春町',
      image: '/assets/scenery/fukushima/scenes/01_takizakura.webp',
      hotspots: [
        {
          id: 'miharu-takizakura-species',
          x: 0.3,
          y: 0.35,
          title: 'エドヒガン系の紅枝垂桜',
          body: '樹種はエドヒガン系の紅枝垂桜（ベニシダレザクラ）。樹齢は1000年以上といわれる。開花は例年4月中旬から下旬。',
          source: 'https://www.town.miharu.fukushima.jp/soshiki/7/02-0101setumei1.html',
          mapQuery: '三春滝桜',
        },
        {
          id: 'miharu-takizakura-size',
          x: 0.6,
          y: 0.6,
          title: '樹高13.5メートル、根回り11.3メートル',
          body: '樹高13.5メートル、根回り11.3メートル。枝張りは幹から北へ5.5メートル、東へ11.0メートル、南へ14.5メートル、西へ14.0メートルと、方角で大きく違う。',
          source: 'https://www.town.miharu.fukushima.jp/soshiki/19/digital-museum030.html',
          mapQuery: '三春町 滝桜',
        },
        {
          id: 'miharu-takizakura-designation',
          x: 0.8,
          y: 0.28,
          title: '大正11年の天然記念物',
          body: '大正11年（1922年）10月12日に国の天然記念物に指定された。根尾谷の淡墨ザクラ、山高神代ザクラと並んで日本三大桜に数えられる。',
          source: 'https://www.town.miharu.fukushima.jp/soshiki/19/digital-museum030.html',
          mapQuery: '三春滝桜 駐車場',
        },
      ],
    },
    {
      id: 'oze',
      name: '尾瀬ヶ原',
      area: '檜枝岐村',
      image: '/assets/scenery/fukushima/scenes/02_oze.webp',
      hotspots: [
        {
          id: 'oze-size',
          x: 0.28,
          y: 0.35,
          title: '本州最大の高層湿原',
          body: '長さ6キロ、幅2キロにおよぶ、本州最大の高層湿原です。',
          source: 'https://www.pref.fukushima.lg.jp/w4/oze/about/nature.html',
          mapQuery: '尾瀬ヶ原',
        },
        {
          id: 'oze-peat',
          x: 0.55,
          y: 0.75,
          title: '1センチの足跡に10年',
          body: 'およそ8000年前から泥炭の形成が始まり、現在は1年間に0.7ミリずつ積もっている。1センチの足跡を残したなら、その回復には10年以上の歳月が必要になる。',
          source: 'https://www.pref.fukushima.lg.jp/w4/oze/about/nature.html',
          mapQuery: '尾瀬ヶ原 木道',
        },
        {
          id: 'oze-hiuchi',
          x: 0.78,
          y: 0.3,
          title: '東北の最高峰',
          body: '奥に立つ燧ヶ岳は、最高点の柴安嵓が標高2,356メートル。福島県の最高峰で、東北の最高峰でもあり、尾瀬のシンボルのような山です。',
          source: 'https://www.tif.ne.jp/yamafuku/mt30/20.html',
          mapQuery: '燧ヶ岳',
        },
      ],
    },
    {
      id: 'goshikinuma',
      name: '五色沼湖沼群',
      area: '北塩原村',
      image: '/assets/scenery/fukushima/scenes/03_goshikinuma.webp',
      hotspots: [
        {
          id: 'goshikinuma-origin',
          x: 0.28,
          y: 0.35,
          title: '1888年の山体崩壊',
          body: '1888年（明治21年）に磐梯山の北側、小磐梯を含む部分が水蒸気爆発によって山体崩壊を起こし、岩なだれが川をせき止めて、このあたりに数百もの湖沼ができた。',
          source: 'https://www.urabandai-inf.com/?page_id=141',
          mapQuery: '五色沼湖沼群',
        },
        {
          id: 'goshikinuma-names',
          x: 0.55,
          y: 0.6,
          title: 'ひとつの沼ではない',
          body: '五色沼は毘沙門沼・赤沼・みどろ沼・竜沼・弁天沼・るり沼・青沼・柳沼など、数多くの湖沼の総称である。',
          source: 'https://www.urabandai-inf.com/?page_id=141',
          mapQuery: '毘沙門沼',
        },
        {
          id: 'goshikinuma-colour',
          x: 0.78,
          y: 0.8,
          title: '色が沼ごとに違う',
          body: 'エメラルドグリーン、コバルトブルー、ターコイズブルー、エメラルドブルー、パステルブルー。色が違う理由は、天候や季節、見る角度、水中に含まれる火山性物質などによると言われている。探勝路は全長約4キロ。',
          source: 'https://www.urabandai-inf.com/?page_id=141',
          mapQuery: '五色沼自然探勝路',
        },
      ],
    },
    {
      id: 'jododaira',
      name: '浄土平と吾妻小富士',
      area: '福島市',
      image: '/assets/scenery/fukushima/scenes/04_jododaira.webp',
      hotspots: [
        {
          id: 'jododaira-crater',
          x: 0.6,
          y: 0.35,
          title: '噴火口を一周できる',
          body: '吾妻小富士では大きな噴火口を間近にのぞくことができ、噴火口は約40分ほどで一周することができる。登りは丸太の階段をジグザグと約10分。',
          source: 'https://www.env.go.jp/park/bandai/guide/joudodaira/course/course05.html',
          mapQuery: '吾妻小富士',
        },
        {
          id: 'jododaira-marsh',
          x: 0.28,
          y: 0.62,
          title: '一周15分の湿原',
          body: '浄土平湿原は一周約15分の周遊コースで、一部の区間は車いすにも対応している。6月下旬から7月上旬になると、ワタスゲの白い花穂が緑のなかに模様を描く。',
          source: 'https://www.env.go.jp/park/bandai/guide/joudodaira/course/course05.html',
          mapQuery: '浄土平湿原',
        },
        {
          id: 'jododaira-road',
          x: 0.8,
          y: 0.8,
          title: '名づけたのは井上靖',
          body: '磐梯吾妻スカイラインは日本の道100選にも選ばれた観光山岳道路。沿線の8つの景勝地を、作家の井上靖が「吾妻八景」と名づけた。白樺の峰、つばくろ谷、天狗の庭、浄土平、双竜の辻、湖見峠、天風境、国見台の8つである。',
          source: 'https://www.city.fukushima.fukushima.jp/soshiki/5/1019/3/2691.html',
          mapQuery: '磐梯吾妻スカイライン',
        },
      ],
    },
    {
      id: 'iimoriyama',
      name: '飯盛山',
      area: '会津若松市',
      image: '/assets/scenery/fukushima/scenes/05_iimoriyama.webp',
      solemn: true,
      hotspots: [
        {
          id: 'iimoriyama-view',
          x: 0.5,
          y: 0.45,
          title: 'ここから城が見えた',
          body: '白虎隊士は戸ノ口堰の洞門をくぐって飯盛山にたどり着くと、鶴ヶ城の天守を覆い隠すもうもうたる黒煙を目にした。会津若松市の計画書は、そのうえで彼らが選んだのは自刃であったと記している。',
          source:
            'https://www.city.aizuwakamatsu.fukushima.jp/docs/2023042400018/file_contents/08ni4.pdf',
          mapQuery: '飯盛山 会津若松市',
        },
        {
          id: 'iimoriyama-graves',
          x: 0.25,
          y: 0.7,
          title: '五十基の墓碑',
          body: '山の中腹には、鶴ヶ城を望んで自刃した19人の白虎隊士と、各地で戦死した31人の白虎隊士の墓碑群がある。正面の白虎隊十九士の墓は明治23年（1890年）に現在の形に整えられた。',
          source:
            'https://www.city.aizuwakamatsu.fukushima.jp/docs/2023042400018/file_contents/08ni4.pdf',
          mapQuery: '白虎隊十九士の墓',
        },
        {
          id: 'iimoriyama-memorial',
          x: 0.78,
          y: 0.72,
          title: '年に二度の墓前祭',
          body: '毎年4月24日と9月24日の春秋2回、神式による白虎隊士墓前祭が行われている。会津若松市は、この一帯を「先人慰霊にみる歴史的風致」として計画に位置づけている。',
          source:
            'https://www.city.aizuwakamatsu.fukushima.jp/docs/2023042400018/file_contents/08ni4.pdf',
          mapQuery: '飯盛山',
        },
      ],
    },
    {
      id: 'tonohetsuri',
      name: '塔のへつり',
      area: '下郷町',
      image: '/assets/scenery/fukushima/scenes/06_tonohetsuri.webp',
      hotspots: [
        {
          id: 'tonohetsuri-designation',
          x: 0.25,
          y: 0.3,
          title: '昭和18年に天然記念物',
          body: '昭和18年に国の天然記念物に指定された。奇岩怪石が、巨大な石の塔のようにそそり立っている。',
          source: 'https://www.town.shimogo.fukushima.jp/organization/sougouseisaku/1/2/590.html',
          mapQuery: '塔のへつり',
        },
        {
          id: 'tonohetsuri-erosion',
          x: 0.55,
          y: 0.6,
          title: '百万年の浸食と風化',
          body: '大川（阿賀川）が百万年の歳月をかけて浸食と風化をくり返してできた景観で、奇岩怪石が並ぶ。',
          source: 'https://www.town.shimogo.fukushima.jp/organization/sougouseisaku/1/2/590.html',
          mapQuery: '下郷町 塔のへつり',
        },
        {
          id: 'tonohetsuri-season',
          x: 0.78,
          y: 0.78,
          title: '初夏は藤の花',
          body: '初夏には、藤の花と白い岩肌が織りなす眺めが楽しめる。大川羽鳥県立公園のなかにあり、大川ラインの一番の景勝地とされる。',
          source: 'https://www.pref.fukushima.lg.jp/sec/41360a/furusato-52.html',
          mapQuery: '塔のへつり 下郷町',
        },
      ],
    },
    {
      id: 'abukumado',
      name: 'あぶくま洞',
      area: '田村市',
      image: '/assets/scenery/fukushima/scenes/07_abukumado.webp',
      hotspots: [
        {
          id: 'abukumado-length',
          x: 0.28,
          y: 0.32,
          title: '全長約600メートル',
          body: 'あぶくま洞の全長は約600メートル。',
          source: 'https://www.city.tamura.lg.jp/soshiki/19/abukuma-syounyuudou-top.html',
          mapQuery: 'あぶくま洞',
        },
        {
          id: 'abukumado-hall',
          x: 0.55,
          y: 0.6,
          title: '天井まで約30メートル',
          body: '三層に分かれる洞内で最大のホール「滝根御殿」は、天井までの高さが約30メートルあり、洞内でいちばん大きな空間になっている。',
          source: 'https://www.city.tamura.lg.jp/soshiki/19/abukuma-syounyuudou-top.html',
          mapQuery: 'あぶくま洞 滝根御殿',
        },
        {
          id: 'abukumado-forms',
          x: 0.8,
          y: 0.8,
          title: '名のついた鍾乳石',
          body: 'クリスタルカーテン、ボックスワーク、シールド、洞穴サンゴといった形が見られ、ほかに竜宮殿、月の世界、樹氷、クリスマスツリー、石化の樹林などの名がついた見どころがある。',
          source: 'https://www.city.tamura.lg.jp/soshiki/19/abukuma-syounyuudou-top.html',
          mapQuery: '田村市 あぶくま洞',
        },
      ],
    },
    {
      id: 'inawashiroko',
      name: '猪苗代湖',
      area: '猪苗代町',
      image: '/assets/scenery/fukushima/scenes/08_inawashiroko.webp',
      hotspots: [
        {
          id: 'inawashiroko-ramsar',
          x: 0.28,
          y: 0.3,
          title: '2025年にラムサール条約湿地',
          body: '令和7年7月15日にラムサール条約湿地に登録された。面積は10,960ヘクタールで、郡山市、会津若松市、猪苗代町にまたがる。',
          source: 'https://www.env.go.jp/press/press_00223.html',
          mapQuery: '猪苗代湖',
        },
        {
          id: 'inawashiroko-birds',
          x: 0.55,
          y: 0.7,
          title: 'ハクチョウとカモの越冬地',
          body: 'ハクチョウ類やカモ類にとって重要な越冬地とされている。',
          source: 'https://www.env.go.jp/press/press_00223.html',
          mapQuery: '猪苗代湖 白鳥浜',
        },
        {
          id: 'inawashiroko-frozen',
          x: 0.8,
          y: 0.5,
          title: '全面凍結しない湖',
          body: '猪苗代湖は全面凍結しない不凍湖で、流れ込む河川の河口付近には多くの砂浜ができている。',
          source: 'https://www.env.go.jp/press/press_00223.html',
          mapQuery: '猪苗代町 湖畔',
        },
      ],
    },
    {
      id: 'tsurugajo',
      name: '鶴ヶ城',
      area: '会津若松市',
      image: '/assets/scenery/fukushima/scenes/09_tsurugajo.webp',
      hotspots: [
        {
          id: 'tsurugajo-history',
          x: 0.28,
          y: 0.35,
          title: '黒川城から若松城へ',
          body: '至徳元年（1384年）に葦名直盛が築いた黒川城が前身。天正18年（1590年）に蒲生氏郷が入城し、近世城郭に改築して七層の天守を完成させ、名を若松城と改めた。',
          source: 'https://www.city.aizuwakamatsu.fukushima.jp/docs/2012110600030/',
          mapQuery: '鶴ヶ城',
        },
        {
          id: 'tsurugajo-rebuilt',
          x: 0.55,
          y: 0.62,
          title: 'いまの天守は1965年の再建',
          body: '昭和40年（1965年）に天守閣が再建され、平成13年（2001年）には南走長屋と干飯櫓が復元された。江戸時代から残る建物ではない。',
          source: 'https://www.city.aizuwakamatsu.fukushima.jp/docs/2012110600030/',
          mapQuery: '鶴ヶ城 会津若松市',
        },
        {
          id: 'tsurugajo-red-tile',
          x: 0.78,
          y: 0.28,
          title: '赤瓦は2011年から',
          body: '平成23年（2011年）に天守閣の屋根を45年ぶりに葺き替え、それまでの黒瓦を赤瓦にした。赤瓦は会津松平藩の藩祖である保科正之の時代から葺かれていたことが記録に残っている。',
          source: 'https://www.city.aizuwakamatsu.fukushima.jp/docs/2012110600030/',
          mapQuery: '鶴ヶ城 天守閣',
        },
      ],
    },
    {
      id: 'ouchijuku',
      name: '大内宿',
      area: '下郷町',
      image: '/assets/scenery/fukushima/scenes/10_ouchijuku.webp',
      hotspots: [
        {
          id: 'ouchijuku-district',
          x: 0.28,
          y: 0.35,
          title: '昭和56年に選定',
          body: '昭和56年4月18日に、国の重要伝統的建造物群保存地区に選定された。',
          source: 'https://www.town.shimogo.fukushima.jp/organization/kyouiku/4/150.html',
          mapQuery: '大内宿',
        },
        {
          id: 'ouchijuku-houses',
          x: 0.55,
          y: 0.6,
          title: '44棟が街道の両側に',
          body: '宿場を中心とした南北500メートル、東西200メートルの範囲に、旧街道の両側に44棟の主屋が立ち並ぶ。建物は茅葺きの寄棟造りで、妻を旧街道に向けている。',
          source: 'https://www.town.shimogo.fukushima.jp/organization/kyouiku/4/150.html',
          mapQuery: '大内宿 下郷町',
        },
        {
          id: 'ouchijuku-hanjuku',
          x: 0.78,
          y: 0.78,
          title: '半宿半農の集落',
          body: '会津藩が会津五街道のひとつとして整えた下野街道の主要な宿駅で、若松城下から第三の宿駅にあたる。住民は荷役や人馬の継ぎ立てと宿場の経営のかたわら、高地で農業をする半宿半農の集落だった。明治17年（1884年）に国道121号が別のルートで開通すると、幹線道路からはずれた山あいの農村になった。',
          source: 'https://www.town.shimogo.fukushima.jp/organization/kyouiku/4/150.html',
          mapQuery: '大内宿 雪まつり',
        },
      ],
    },
  ],
};
